import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CATEGORIES, LOCATIONS, LEVELS, toggleIn, listOf } from "@/lib/api";

const empty = {
  title: "", notes: "", categories: ["Personal / home"], urgency: "medium",
  energy_required: "medium", estimated_minutes: 15, locations: ["home"],
  due_date: "", must_do_today: false,
};

const Pills = ({ label, options, value, onChange, testid, multi }) => (
  <div>
    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{label}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testid}-${opt}`}
          onClick={() => onChange(opt)}
          className={`px-3 py-2 rounded-xl text-sm font-medium capitalize transition-colors ${
            (multi ? value.includes(opt) : value === opt)
              ? "bg-[#365458] text-white"
              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export const TaskForm = ({ open, onOpenChange, onSubmit, initial }) => {
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (open) {
      setForm(initial
        ? {
            ...empty, ...initial,
            due_date: initial.due_date || "",
            categories: listOf(initial, "categories", "category"),
            locations: listOf(initial, "locations", "location"),
          }
        : empty);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.title.trim()) return;
    onSubmit({
      ...form,
      estimated_minutes: Number(form.estimated_minutes) || 15,
      due_date: form.due_date || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto no-scrollbar rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {initial ? "Edit task" : "New task"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <Input
            data-testid="task-title-input"
            placeholder="What needs doing?"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-zinc-100 h-12 text-base rounded-xl"
          />
          <Textarea
            data-testid="task-notes-input"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            className="bg-zinc-800 border-zinc-700 text-zinc-100 rounded-xl resize-none"
          />
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Category</p>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`task-category-${c}`}
                  onClick={() => set("categories", toggleIn(form.categories, c))}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors ${
                    form.categories.includes(c) ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <Pills label="Urgency" options={LEVELS} value={form.urgency} onChange={(v) => set("urgency", v)} testid="task-urgency" />
          <Pills label="Energy needed" options={LEVELS} value={form.energy_required} onChange={(v) => set("energy_required", v)} testid="task-energy" />
          <Pills label="Location" options={LOCATIONS} value={form.locations} multi
            onChange={(v) => set("locations", toggleIn(form.locations, v))} testid="task-location" />
          <div className="flex gap-4">
            <div className="flex-1">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Minutes</p>
              <Input
                data-testid="task-minutes-input"
                type="number"
                value={form.estimated_minutes}
                onChange={(e) => set("estimated_minutes", e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-11 rounded-xl"
              />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Due date</p>
              <Input
                data-testid="task-due-input"
                type="date"
                value={form.due_date}
                onChange={(e) => set("due_date", e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 h-11 rounded-xl"
              />
            </div>
          </div>
          <button
            type="button"
            data-testid="task-mustdo-toggle"
            onClick={() => set("must_do_today", !form.must_do_today)}
            className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${
              form.must_do_today ? "bg-[#365458] text-white" : "bg-zinc-800 text-zinc-300"
            }`}
          >
            {form.must_do_today ? "★ Must do today" : "Mark as must do today"}
          </button>
          <Button
            data-testid="task-save-button"
            onClick={submit}
            className="w-full h-14 rounded-2xl bg-[#365458] hover:bg-[#2A4346] text-white text-base font-bold"
          >
            {initial ? "Save changes" : "Add task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
